import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from './Navbar';
import SideMenu from './SideMenu';
import useAutoTranslate from '../hooks/useAutoTranslate';
import { Button, BusyIndicator, Card, Dialog, Icon, Label, Option, Select, Title, Text } from '@ui5/webcomponents-react';
import {
  fetchSalesOrderById,
  fetchSalesOrderByOrderNumber,
  isSalesOrderUuidKey,
  mapSalesOrderDetailApiToUi,
  getSalesOrderListStatusKind,
  getSalesOrderSapDocNumForDisplay,
  canCancelSalesOrderStatus,
  resolveSalesOrderDetailWorkflowActiveStep,
  cancelSalesOrdersByIds,
  fetchTotalUnitsAcrossOcReference,
  sumSalesOrderDetailLineQuantities,
  sumSalesOrderDetailReturnedQuantities,
  sumSalesOrderDetailShippedQuantities
} from '../services/salesOrdersList';
import {
  fetchSapB1BusinessPartnerByFederalTaxId,
  formatSapB1BillingAddressLine,
  normalizeFederalTaxIdForQuery
} from '../services/sapB1BusinessPartners';
import { getLoggedInUserId } from '../utils/userSession';
import { getStatusStyle } from '../utils/validator';

const pageBg = '#f4f7f9';
const cardRadius = 10;
const cardShadow = '0 1px 4px rgba(0,0,0,0.06)';
const primaryBtn = '#2F5A88';

/** Tamaños de página de líneas en detalle OV (mismo criterio que el listado `SalesOrderList`). */
const SALES_ORDER_LINES_PAGE_SIZES = [15, 30, 50, 100];

/**
 * Detalle de una OV: datos vía `GET sales-orders/:id` (`fetchSalesOrderById` +
 * `mapSalesOrderDetailApiToUi` en `../services/salesOrdersList.js`).
 *
 * El parámetro de ruta `:id` es el **número de orden** negocio (URL codificada).
 *
 * **Stepper:** cronología visible de **3 pasos** (Creada/Cancelada → Aceptada → Despacho según estado); círculos con 1–3 o ✓ si el paso ya se completó; paso activo =
 * `Math.min(3, resolveSalesOrderDetailWorkflowActiveStep(...))` para colapsar pasos 4–5 del API en el último paso visible.
 * Versiones anteriores (8 y 5 pasos) conservadas comentadas en código.
 *
 * **Cliente:** opcionalmente se enriquece con `fetchSapB1BusinessPartnerByFederalTaxId` (RUT) para datos SAP B1.
 *
 * **Cancelar:** solo si `canCancelSalesOrderStatus` (`PENDING`/`ACCEPTED`); en otro caso «Estado» es solo lectura.
 *
 * **Total unidades por OC:** se usa `totalUnitsByOcReference` del `GET` detalle (y `shipping` / alias) para no
 * llamar a `fetchTotalUnitsAcrossOcReference`. Si el detalle aún no lo trae, se acepta el valor en
 * `location.state` al abrir desde el listado (cabeceras `sales-orders/headers`).
 */
const SalesOrderDetail = () => {
  const { autoT } = useAutoTranslate();
  const { id } = useParams();
  const { state: locationState, key: locationKey } = useLocation();
  const navigate = useNavigate();
  /**
   * Vuelve al mismo contexto que al abrir OV: `/home` con pestaña «Ordenes» y subpestaña OV (`?view=sales` en `BuyOrderList`).
   * No usar `/buy-order-list`: esa ruta no incluye el TabContainer de `Home` (Inicio, Ordenes, …).
   */
  const goBackToSalesOrdersList = useCallback(() => {
    navigate({ pathname: '/home', search: '?view=sales' }, { state: { selectedTab: 'buyorder' } });
  }, [navigate]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  /** Orden ya mapeada al shape del mock (`OV_DETAIL`) para no reescribir el JSX del detalle. */
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [linesPage, setLinesPage] = useState(1);
  const [linesPageSize, setLinesPageSize] = useState(15);
  const [sapPartner, setSapPartner] = useState(null);
  const [sapPartnerLoading, setSapPartnerLoading] = useState(false);
  /** Total unidades agregado por OC de referencia (todas las OV del mismo prefijo); ver `fetchTotalUnitsAcrossOcReference`. */
  const [totalUnitsOc, setTotalUnitsOc] = useState(null);
  const [totalUnitsOcLoading, setTotalUnitsOcLoading] = useState(false);
  const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
  const [cancelOrderLoading, setCancelOrderLoading] = useState(false);
  const [showCancelOrderResultDialog, setShowCancelOrderResultDialog] = useState(false);
  const [cancelOrderResultSummary, setCancelOrderResultSummary] = useState(null);

  const totalShippedUnits = useMemo(
    () => sumSalesOrderDetailShippedQuantities(order?.lines),
    [order?.lines]
  );
  const totalReturnedUnits = useMemo(
    () => sumSalesOrderDetailReturnedQuantities(order?.lines),
    [order?.lines]
  );

  /** Recarga datos de la OV actual sin apagar toda la pantalla (tras cancelar, etc.). */
  const fetchSalesOrderDetailRaw = useCallback(async (rawKey) => {
    const k = rawKey ? decodeURIComponent(String(rawKey)).trim() : '';
    if (!k) return null;
    return isSalesOrderUuidKey(k)
      ? fetchSalesOrderById(k)
      : fetchSalesOrderByOrderNumber(k);
  }, []);

  const silentRefreshOrder = useCallback(async () => {
    const rawId = id ? decodeURIComponent(String(id)).trim() : '';
    if (!rawId) return;
    try {
      const raw = await fetchSalesOrderDetailRaw(rawId);
      if (!raw) return;
      const mapped = mapSalesOrderDetailApiToUi(raw);
      setOrder(mapped);
    } catch {
      /* conservar estado previo */
    }
  }, [id, fetchSalesOrderDetailRaw]);

  const closeCancelOrderDialog = useCallback(() => {
    if (cancelOrderLoading) return;
    setIsCancelOrderDialogOpen(false);
  }, [cancelOrderLoading]);

  const handleConfirmCancelOrderFromDetail = useCallback(async () => {
    if (!order) return;
    const orderKey = String(order.id ?? order.orderNumber ?? '').trim();
    if (!orderKey) return;
    setCancelOrderLoading(true);
    try {
      const mb = getLoggedInUserId();
      const results = await cancelSalesOrdersByIds([orderKey], mb ? { modifiedBy: mb } : {});
      const failures = results.filter((r) => r.outcome === 'error');
      const successCount = results.filter((r) => r.outcome === 'ok').length;
      setIsCancelOrderDialogOpen(false);
      await silentRefreshOrder();
      if (failures.length > 0) {
        setCancelOrderResultSummary({ successCount, failures });
        setShowCancelOrderResultDialog(true);
      }
    } catch (e) {
      setCancelOrderResultSummary({
        successCount: 0,
        failures: [{ id: orderKey, errorMessage: String(e?.message || e) }]
      });
      setShowCancelOrderResultDialog(true);
      setIsCancelOrderDialogOpen(false);
    } finally {
      setCancelOrderLoading(false);
    }
  }, [order, silentRefreshOrder]);

  // Carga detalle OV al montar o al cambiar `:id` en la URL. Sin `autoT` en deps (evita bucles de fetch).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const rawId = id ? decodeURIComponent(String(id)).trim() : '';
      if (!rawId) {
        setOrder(null);
        setLoadError('');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setLoadError('');
      try {
        const raw = await fetchSalesOrderDetailRaw(rawId);
        if (!raw) return;
        const mapped = mapSalesOrderDetailApiToUi(raw);
        if (mapped && (mapped.totalUnitsByOcReference == null || !Number.isFinite(mapped.totalUnitsByOcReference))) {
          const st = locationState?.totalUnitsByOcReference;
          if (st != null && st !== '') {
            const n =
              typeof st === 'number' && Number.isFinite(st)
                ? st
                : parseFloat(String(st).replace(',', '.'), 10);
            if (Number.isFinite(n)) {
              mapped.totalUnitsByOcReference = n;
            }
          }
        }
        if (cancelled) return;
        setOrder(mapped);
      } catch (e) {
        if (!cancelled) {
          setOrder(null);
          const data = e?.response?.data;
          const msg =
            (typeof data?.message === 'string' && data.message) ||
            (typeof data?.error === 'string' && data.error) ||
            e?.message ||
            '';
          setLoadError(msg || 'No se pudo cargar el detalle de la orden');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, fetchSalesOrderDetailRaw, locationKey, locationState?.totalUnitsByOcReference]);

  useEffect(() => {
    setLinesPage(1);
    setLinesPageSize(15);
  }, [id]);

  const handleLinesPageSizeChange = useCallback((e) => {
    const raw = e.detail?.selectedOption?.value;
    const n = parseInt(String(raw ?? ''), 10);
    if (!Number.isFinite(n) || !SALES_ORDER_LINES_PAGE_SIZES.includes(n)) return;
    setLinesPageSize(n);
    setLinesPage(1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!order) {
      setSapPartner(null);
      setSapPartnerLoading(false);
      return undefined;
    }
    const rutRaw = order.client?.rut;
    if (!normalizeFederalTaxIdForQuery(rutRaw)) {
      setSapPartner(null);
      setSapPartnerLoading(false);
      return undefined;
    }
    setSapPartner(null);
    setSapPartnerLoading(true);
    fetchSapB1BusinessPartnerByFederalTaxId(rutRaw)
      .then((data) => {
        if (!cancelled) {
          setSapPartner(data);
          setSapPartnerLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSapPartner(null);
          setSapPartnerLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [order]);

  useEffect(() => {
    if (!order?.orderNumber) {
      setTotalUnitsOc(null);
      setTotalUnitsOcLoading(false);
      return undefined;
    }
    if (order.totalUnitsByOcReference != null && Number.isFinite(order.totalUnitsByOcReference)) {
      setTotalUnitsOc(order.totalUnitsByOcReference);
      setTotalUnitsOcLoading(false);
      return undefined;
    }
    let cancelled = false;
    setTotalUnitsOc(null);
    setTotalUnitsOcLoading(true);
    fetchTotalUnitsAcrossOcReference(order.orderNumber)
      .then((n) => {
        if (!cancelled) setTotalUnitsOc(n);
      })
      .catch(() => {
        if (!cancelled) setTotalUnitsOc(sumSalesOrderDetailLineQuantities(order.lines));
      })
      .finally(() => {
        if (!cancelled) setTotalUnitsOcLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order]);

  useEffect(() => {
    if (!showTrackingModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowTrackingModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showTrackingModal]);

  /*
   * Cronología OV anterior — 8 pasos (conservada comentada; ya no se renderiza):
   * const workflowLabels = useMemo(() => {
   *   const kind = order ? getSalesOrderListStatusKind(order) : 'created';
   *   const sapDoc = getSalesOrderSapDocNumForDisplay(order);
   *   const sentToSap = kind === 'sent_sap';
   *   const step2Label = sentToSap
   *     ? autoT('Aceptada en SAP DocNum {{doc}}', { doc: sapDoc })
   *     : autoT('Aceptada');
   *   return [
   *     kind === 'canceled' ? autoT('Cancelada') : autoT('Creada'),
   *     step2Label,
   *     autoT('Integrada'),
   *     autoT('Bodega'),
   *     autoT('Despachada'),
   *     autoT('En camino'),
   *     autoT('Entregado'),
   *     autoT('Completado')
   *   ];
   * }, [autoT, order?.status, order?.docNumberReference, order?.externalOrderId]);
   */

  /*
   * Stepper OV — cinco pasos visibles (ya no en UI): paso 1 y 3 dinámicos + En camino + Entregada.
   * const workflowLabelsFiveStep = useMemo(() => {
   *   const kind = order ? getSalesOrderListStatusKind(order) : 'created';
   *   const step1 = kind === 'canceled' ? autoT('Cancelada') : autoT('Creada');
   *   let step3 = autoT('Despachada');
   *   if (kind === 'partially_shipped') step3 = autoT('Despacho Parcial');
   *   else if (kind === 'returned') step3 = autoT('Devuelta');
   *   else if (kind === 'shipped') step3 = autoT('Despachada');
   *   return [step1, autoT('Aceptada'), step3, autoT('En camino'), autoT('Entregada')];
   * }, [autoT, order?.status]);
   */

  /** Tres pasos visibles: paso 1 y 3 con texto según `status` de la OV. */
  const workflowLabelsThreeStep = useMemo(() => {
    const kind = order ? getSalesOrderListStatusKind(order) : 'created';
    const step1 = kind === 'canceled' ? autoT('Cancelada') : autoT('Creada');
    let step3 = autoT('Despachada');
    if (kind === 'partially_shipped') step3 = autoT('Despacho Parcial');
    else if (kind === 'closed') step3 = autoT('Cerrado');
    else if (kind === 'returned') step3 = autoT('Devuelta');
    else if (kind === 'shipped') step3 = autoT('Despachada');
    return [step1, autoT('Aceptada'), step3];
  }, [autoT, order?.status]);

  const toggleSidebar = () => setSidebarOpen((o) => !o);

  const pill = (text, bg, color) => (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: 14,
        fontSize: '0.8rem',
        fontWeight: 600,
        background: bg,
        color
      }}
    >
      {text}
    </span>
  );

  const summaryMiniCard = (label, value, valueStyle = {}, surfaceStyle = {}) => (
    <div
      style={{
        flex: '1 1 220px',
        background: '#fff',
        borderRadius: cardRadius,
        boxShadow: cardShadow,
        padding: '0.75rem 1rem',
        minWidth: 0,
        ...surfaceStyle
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: '0.35rem',
          columnGap: '0.5rem'
        }}
      >
        <Text style={{ color: '#6c757d', fontSize: '0.85rem', fontWeight: 600, flexShrink: 0 }}>
          {label}:
        </Text>
        <Text style={{ fontSize: '0.95rem', fontWeight: 600, ...valueStyle, wordBreak: 'break-word' }}>
          {value != null && String(value).trim() !== '' ? value : '—'}
        </Text>
      </div>
    </div>
  );

  const detailFieldInline = (label, value, valueExtraStyle = {}) => (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: '0.35rem',
          columnGap: '0.45rem'
        }}
      >
        <Text style={{ color: '#6c757d', fontSize: '0.8rem', fontWeight: 600, flexShrink: 0 }}>
          {label}:
        </Text>
        <Text
          style={{
            fontSize: '0.95rem',
            fontWeight: 500,
            lineHeight: 1.35,
            ...valueExtraStyle,
            wordBreak: 'break-word'
          }}
        >
          {value != null && String(value).trim() !== '' ? value : '—'}
        </Text>
      </div>
    </div>
  );

  const detailFieldsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '0.85rem 1rem',
    alignItems: 'start'
  };

  const headerNavy = '#1a2b45';

  const renderTimelineDot = (state) => {
    if (state === 'done') {
      return (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#27ae60',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.85rem',
            fontWeight: 700,
            flexShrink: 0
          }}
        >
          ✓
        </div>
      );
    }
    if (state === 'current') {
      return (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#2980b9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <div
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: '#fff'
            }}
          />
        </div>
      );
    }
    return (
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: '2px solid #cfd8dc',
          background: '#fff',
          boxSizing: 'border-box',
          flexShrink: 0
        }}
      />
    );
  };

  const renderTrackingModal = () => {
    if (!showTrackingModal || !order) return null;
    const events = order.trackingEvents || [];
    const lastUp = order.trackingLastUpdate || '';

    return (
      <div
        role="presentation"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 11000,
          background: 'rgba(15, 23, 42, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}
        onClick={() => setShowTrackingModal(false)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="tracking-modal-title"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 'min(520px, 100%)',
            maxHeight: 'min(640px, 92vh)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              background: headerNavy,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem',
              flexShrink: 0,
              flexWrap: 'wrap'
            }}
          >
            <Icon name="shipping-status" style={{ color: '#fff', flexShrink: 0 }} />
            <span
              id="tracking-modal-title"
              style={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '1rem',
                flex: 1,
                minWidth: 0
              }}
            >
              {autoT('Seguimiento de envío')}
            </span>
            <span
              style={{
                background: 'rgba(255,255,255,0.18)',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: 14,
                fontSize: '0.8rem',
                fontWeight: 600,
                flexShrink: 0
              }}
            >
              {order.trackingNumber || order.shipping?.tracking || '—'}
            </span>
            <Button
              design="Transparent"
              icon="decline"
              style={{ color: '#fff', minWidth: '2rem' }}
              onClick={() => setShowTrackingModal(false)}
            />
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1.25rem 1.25rem 1rem',
              background: '#fff'
            }}
          >
            {events.map((ev, i) => {
              const pending = ev.state === 'pending';
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 14,
                    opacity: pending ? 0.55 : 1
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: 28,
                      flexShrink: 0
                    }}
                  >
                    {renderTimelineDot(ev.state)}
                    {i < events.length - 1 ? (
                      <div
                        style={{
                          width: 2,
                          flex: 1,
                          minHeight: 20,
                          background: '#e0e4e8',
                          marginTop: 4
                        }}
                      />
                    ) : null}
                  </div>
                  <div style={{ flex: 1, paddingBottom: i < events.length - 1 ? 20 : 0 }}>
                    <Text style={{ fontSize: '0.75rem', color: '#90a4ae', marginBottom: 4 }}>
                      {ev.datetime || '— —'}
                    </Text>
                    <Text
                      style={{
                        display: 'block',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#37474f',
                        marginBottom: 6
                      }}
                    >
                      {autoT(ev.title)}
                    </Text>
                    {ev.description ? (
                      <Text style={{ display: 'block', fontSize: '0.85rem', color: '#607d8b', lineHeight: 1.45, marginBottom: 8 }}>
                        {autoT(ev.description)}
                      </Text>
                    ) : null}
                    {ev.location ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: '#e8f4fc',
                          color: '#1565c0',
                          padding: '4px 10px',
                          borderRadius: 14,
                          fontSize: '0.78rem',
                          fontWeight: 600
                        }}
                      >
                        <Icon name="map" style={{ width: '0.75rem', height: '0.75rem' }} />
                        {autoT(ev.location)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              borderTop: '1px solid #e8ecef',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
              background: '#fff',
              flexShrink: 0
            }}
          >
            <Text style={{ fontSize: '0.85rem', color: '#78909c' }}>
              {autoT('Última actualización')}
              {': '}
              <span style={{ fontWeight: 700, color: '#455a64' }}>{lastUp}</span>
            </Text>
            <Button
              design="Transparent"
              onClick={() => setShowTrackingModal(false)}
              style={{
                border: '1px solid #cfd8dc',
                borderRadius: 8,
                background: '#fff',
                color: '#37474f'
              }}
            >
              {autoT('Cerrar')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /*
   * Stepper anterior (8 pasos): ver bloque comentado `workflowLabels` arriba y historial git.
   *
   * Stepper 5 pasos (comentado): `workflowLabelsFiveStep.map`, `current` sin `Math.min(3, …)`, círculos `{done ? '✓' : n}` (1–5 en su UI).
   */

  /** Stepper 3 pasos: índice activo acotado a 1–3 (`Math.min(3, resolveSalesOrderDetailWorkflowActiveStep)`). Círculos: ✓ si hecho, si no el número de paso. */
  const renderStepper = () => {
    if (!order) return null;
    const kind = getSalesOrderListStatusKind(order);
    const currentRaw = resolveSalesOrderDetailWorkflowActiveStep(kind, order.workflowCurrentStep);
    const current = Math.min(3, currentRaw);
    const canceledActive = kind === 'canceled';
    const stPending = getStatusStyle('Pending');
    const stShipped = getStatusStyle('Shipped');
    const stCanceled = getStatusStyle('Canceled');
    const stApproved = getStatusStyle('Approved');
    const stDelivered = getStatusStyle('Delivered');
    const stPartial = getStatusStyle('PartiallyShipped');
    const stClosed = getStatusStyle('Closed');
    const stReturned = getStatusStyle('Returned');

    const activeCirclePalette = (n) => {
      if (canceledActive && n === 1) return stCanceled;
      if (n === 1) return stPending;
      if (n === 2) return stShipped;
      if (n === 3) {
        if (kind === 'partially_shipped') return stPartial;
        if (kind === 'closed') return stClosed;
        if (kind === 'returned') return stReturned;
        return stDelivered;
      }
      /* Paso visual 4–5 solo en stepper de 5 pasos (ya no en UI): return stShipped; */
      return stDelivered;
    };

    return (
      <div style={{ marginTop: '1.25rem' }}>
        <Text
          style={{
            display: 'block',
            color: '#8896a3',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            marginBottom: '1rem'
          }}
        >
          {autoT('ESTADO DE LA ORDEN')}
        </Text>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '0.25rem',
            flexWrap: 'wrap'
          }}
        >
          {workflowLabelsThreeStep.map((label, idx) => {
            const n = idx + 1;
            const done = n < current;
            const active = n === current;
            const palette = activeCirclePalette(n);
            const circleStyle = {
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.9rem',
              flexShrink: 0,
              ...(done
                ? { background: stApproved.color, color: '#fff' }
                : active
                  ? { background: palette.color, color: '#fff' }
                  : { background: '#e0e4e8', color: '#78909c' })
            };
            return (
              <div
                key={n}
                style={{
                  flex: '1 1 88px',
                  textAlign: 'center',
                  minWidth: 72
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                  <div style={circleStyle}>{done ? '✓' : n}</div>
                </div>
                <Text
                  style={{
                    fontSize: '0.72rem',
                    color: active ? palette.color : '#546e7a',
                    fontWeight: active ? 600 : 400
                  }}
                >
                  {label}
                </Text>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /** Contenedor común (navbar + menú) para estados de carga, error o “no encontrada”. */
  const shell = (children) => (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: pageBg }}>
      <Navbar toggleSidebar={toggleSidebar} />
      <div style={{ display: 'flex', flex: 1, marginTop: '48px' }}>
        <SideMenu isOpen={sidebarOpen} />
        <div
          style={{
            flex: 1,
            marginLeft: sidebarOpen ? '250px' : '0',
            transition: 'margin-left 0.3s',
            padding: '2rem'
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return shell(
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <BusyIndicator active size="M" text={autoT('Cargando orden de venta...')} />
      </div>
    );
  }

  if (loadError) {
    return shell(
      <>
        <Title level="H3" style={{ marginBottom: '1rem' }}>
          {autoT('Error al cargar la orden')}
        </Title>
        <Text style={{ display: 'block', marginBottom: '1rem', color: '#c62828' }}>{loadError}</Text>
        <Button design="Emphasized" onClick={goBackToSalesOrdersList}>
          {autoT('Volver')}
        </Button>
      </>
    );
  }

  if (!order) {
    return shell(
      <>
        <Title level="H3" style={{ marginBottom: '1rem' }}>
          {autoT('Orden de venta no encontrada')}
        </Title>
        <Button design="Emphasized" onClick={goBackToSalesOrdersList}>
          {autoT('Volver')}
        </Button>
      </>
    );
  }

  const lines = order.lines || [];
  const showLineDestinationBranch = lines.some((line) => String(line.destinationBranch || '').trim());
  const client = order.client || {};
  const shipping = order.shipping || {};
  const finance = order.finance || {};

  const sapBillingLine = formatSapB1BillingAddressLine(sapPartner);
  const displayClientCompanyName = sapPartner?.CardName ?? client.companyName;
  const displayClientRut = sapPartner?.FederalTaxID ?? client.rut;
  const displayClientEmail =
    sapPartner?.EmailAddress != null && String(sapPartner.EmailAddress).trim() !== ''
      ? sapPartner.EmailAddress
      : client.email;
  const displayClientPhone = client.phone;
  const displayClientBillingAddress = sapBillingLine || client.billingAddress;

  const linesTotalPages = Math.max(1, Math.ceil(lines.length / linesPageSize));
  const linesPageSafe = Math.min(Math.max(1, linesPage), linesTotalPages);
  const linesPaged = lines.slice(
    (linesPageSafe - 1) * linesPageSize,
    linesPageSafe * linesPageSize
  );
  const linesRangeStart = lines.length === 0 ? 0 : (linesPageSafe - 1) * linesPageSize + 1;
  const linesRangeEnd = Math.min(linesPageSafe * linesPageSize, lines.length);

  /** Suma de cantidades de todas las líneas (unidades a enviar). */
  const totalUnitsToShip = lines.reduce((sum, line) => {
    const q = line.quantity;
    if (typeof q === 'number' && Number.isFinite(q)) return sum + q;
    const n = parseFloat(String(q ?? '').replace(',', '.'), 10);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const totalUnitsToShipDisplay =
    lines.length === 0
      ? '—'
      : Number.isInteger(totalUnitsToShip) && Math.abs(totalUnitsToShip - Math.round(totalUnitsToShip)) < 1e-9
        ? String(Math.round(totalUnitsToShip))
        : totalUnitsToShip.toLocaleString('es-CL', { maximumFractionDigits: 2 });

  const formatUnitsCountDisplay = (n) => {
    if (n == null || !Number.isFinite(n)) return '—';
    return Number.isInteger(n) && Math.abs(n - Math.round(n)) < 1e-9
      ? String(Math.round(n))
      : n.toLocaleString('es-CL', { maximumFractionDigits: 2 });
  };
  const totalUnitsOcDisplay = totalUnitsOcLoading ? '—' : formatUnitsCountDisplay(totalUnitsOc);

  /** Resumen financiero: subtotal / impuesto / total desde `subtotalAmount`, `taxAmount`, `totalAmount` del API (sin recalcular en cliente). La tarjeta «Descuento» está desactivada a petición de producto. */
  const subtotalNetoDisplay = finance.subtotalNeto ?? '—';
  const impuestoDisplay = finance.iva ?? '—';
  const totalConIvaDisplay = finance.total ?? '—';

  const detailStatusKind = getSalesOrderListStatusKind(order);
  const detailStatusLabelText =
    detailStatusKind === 'canceled'
      ? autoT('Cancelada')
      : detailStatusKind === 'shipped'
        ? autoT('Despachada')
        : detailStatusKind === 'partially_shipped'
          ? autoT('Despacho Parcial')
          : detailStatusKind === 'closed'
            ? autoT('Cerrado')
            : detailStatusKind === 'returned'
              ? autoT('Devuelta')
              : detailStatusKind === 'sent_sap'
                ? autoT('Enviada a SAP: DocNum {{doc}}', {
                    doc: getSalesOrderSapDocNumForDisplay(order)
                  })
                : autoT('creada');
  const detailStatusValueStyle =
    detailStatusKind === 'sent_sap'
      ? { color: getStatusStyle('Shipped').color }
      : detailStatusKind === 'canceled'
        ? { color: getStatusStyle('Canceled').color }
        : detailStatusKind === 'shipped'
          ? { color: getStatusStyle('Delivered').color }
          : detailStatusKind === 'partially_shipped'
            ? { color: getStatusStyle('PartiallyShipped').color }
            : detailStatusKind === 'closed'
              ? { color: getStatusStyle('Closed').color }
              : detailStatusKind === 'returned'
                ? { color: getStatusStyle('Returned').color }
                : { color: getStatusStyle('Pending').color };

  /** Misma cápsula que el listado OV: fondo + borde por tipo de estado (pill). */
  const detailEstadoPillStyle =
    detailStatusKind === 'canceled'
      ? getStatusStyle('Canceled')
      : detailStatusKind === 'sent_sap'
        ? getStatusStyle('Shipped')
        : detailStatusKind === 'shipped'
          ? getStatusStyle('Delivered')
          : detailStatusKind === 'partially_shipped'
            ? getStatusStyle('PartiallyShipped')
            : detailStatusKind === 'closed'
              ? getStatusStyle('Closed')
              : detailStatusKind === 'returned'
                ? getStatusStyle('Returned')
                : getStatusStyle('Pending');

  const showEstadoCancelButton = order && canCancelSalesOrderStatus(order.status);

  const docNumRefRaw = order.docNumberReference ?? order.doc_number_reference;
  const showDocNumHeader = docNumRefRaw != null && String(docNumRefRaw).trim() !== '';
  const docNumHeaderDisplay = showDocNumHeader ? String(docNumRefRaw).trim() : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: pageBg }}>
      <Navbar toggleSidebar={toggleSidebar} />
      <div style={{ display: 'flex', flex: 1, marginTop: '48px' }}>
        <SideMenu isOpen={sidebarOpen} />
        <div
          style={{
            flex: 1,
            marginLeft: sidebarOpen ? '250px' : '0',
            transition: 'margin-left 0.3s',
            padding: '1.25rem 1.5rem 2rem',
            maxWidth: '100%'
          }}
        >
          <Button
            design="Transparent"
            icon="nav-back"
            onClick={goBackToSalesOrdersList}
            style={{ marginBottom: '1rem' }}
          >
            {autoT('Volver a Órdenes de Venta')}
          </Button>

          {/* Cabecera principal */}
          <Card style={{ borderRadius: cardRadius, boxShadow: cardShadow, marginBottom: '1rem' }}>
            <div style={{ padding: '1.35rem 1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                <div>
                  <Title level="H2" style={{ margin: 0, fontSize: '1.65rem', color: '#1a2b3c' }}>
                    {autoT('Orden de Venta: {{ov}}', {
                      ov: String(order.headerCode ?? order.id ?? '').trim() || '—'
                    })}
                  </Title>
                  <Text style={{ display: 'block', marginTop: '0.35rem', color: '#546e7a', fontSize: '0.95rem' }}>
                    {order.headerSubtitle}
                  </Text>
                </div>
                {showDocNumHeader ? (
                  <div
                    style={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      textAlign: 'right',
                      alignSelf: 'flex-start'
                    }}
                  >
                    <Title level="H5" style={{ margin: 0, fontSize: '1.05rem', color: '#1a2b3c', fontWeight: 600 }}>
                      {autoT('DocNum SAP')}
                    </Title>
                    <Text
                      style={{
                        display: 'block',
                        marginTop: '0.3rem',
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: '#2F5A88',
                        wordBreak: 'break-word'
                      }}
                    >
                      {docNumHeaderDisplay}
                    </Text>
                  </div>
                ) : null}
                {/* Botón Tracking: oculto temporalmente; reactivar cuando se vuelva a usar el flujo de tracking.
                <Button
                  design="Emphasized"
                  icon="shipping-status"
                  style={{ background: primaryBtn, color: '#fff', flexShrink: 0 }}
                  onClick={() => setShowTrackingModal(true)}
                >
                  {order.trackingButtonText || autoT('Tracking')}
                </Button>
                */}
              </div>
              {renderStepper()}
            </div>
          </Card>

          {/* Resumen cabecera: fechas, totales, moneda y estado (prioridad cancelado, luego SAP como el listado OV). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
            {summaryMiniCard(autoT('Fecha de la OC'), order.createdAt)}
            {summaryMiniCard(autoT('Fecha entrega est.'), order.estimatedDeliveryDate)}
            {summaryMiniCard(autoT('Total'), order.totalFormatted || order.total, { color: '#0070f3' })}
            {summaryMiniCard(autoT('Moneda'), order.currency)}
            {!showEstadoCancelButton ? (
              summaryMiniCard(autoT('Estado'), detailStatusLabelText, detailStatusValueStyle, {
                backgroundColor: detailEstadoPillStyle.backgroundColor,
                border: detailEstadoPillStyle.border,
                boxSizing: 'border-box'
              })
            ) : (
              <button
                type="button"
                onClick={() => setIsCancelOrderDialogOpen(true)}
                disabled={cancelOrderLoading}
                aria-label={autoT('Cancelar esta orden de venta', {})}
                style={{
                  flex: '1 1 220px',
                  backgroundColor: detailEstadoPillStyle.backgroundColor,
                  borderRadius: cardRadius,
                  boxShadow: cardShadow,
                  padding: '0.75rem 1rem',
                  minWidth: 0,
                  border: detailEstadoPillStyle.border,
                  boxSizing: 'border-box',
                  cursor: cancelOrderLoading ? 'wait' : 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                  transition: 'filter 0.15s ease, box-shadow 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!cancelOrderLoading) e.currentTarget.style.filter = 'brightness(0.96)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'none';
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    gap: '0.35rem',
                    columnGap: '0.5rem'
                  }}
                >
                  <Text style={{ color: '#6c757d', fontSize: '0.85rem', fontWeight: 600, flexShrink: 0 }}>
                    {autoT('Estado')}:
                  </Text>
                  <Text
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      ...detailStatusValueStyle,
                      wordBreak: 'break-word'
                    }}
                  >
                    {detailStatusLabelText}
                  </Text>
                </div>
              </button>
            )}
          </div>

          {/* Cliente + Envío */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              marginBottom: '1rem'
            }}
          >
            <Card style={{ flex: '1 1 380px', minWidth: 0, borderRadius: cardRadius, boxShadow: cardShadow }}>
              <div style={{ padding: '1.25rem' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <Icon name="employee" style={{ color: primaryBtn }} />
                  <Title level="H5" style={{ margin: 0 }}>
                    {autoT('Destinos')}
                  </Title>
                  {sapPartnerLoading ? <BusyIndicator active size="Small" /> : null}
                </div>
                <div style={detailFieldsGridStyle}>
                  {detailFieldInline(autoT('Razón Social'), displayClientCompanyName)}
                  {detailFieldInline(autoT('RUT / RUC'), displayClientRut)}
                  {detailFieldInline(autoT('Email'), displayClientEmail)}
                  {detailFieldInline(autoT('Teléfono'), displayClientPhone)}
                  {detailFieldInline(autoT('Dirección facturación'), displayClientBillingAddress)}
                </div>
              </div>
            </Card>
            <Card style={{ flex: '1 1 380px', minWidth: 0, borderRadius: cardRadius, boxShadow: cardShadow }}>
              <div style={{ padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <Icon name="product" style={{ color: primaryBtn }} />
                  <Title level="H5" style={{ margin: 0 }}>
                    {autoT('Información de Envío')}
                  </Title>
                </div>
                <div style={detailFieldsGridStyle}>
                  {detailFieldInline(autoT('Sucursal Destino'), order.clientName)}
                  {detailFieldInline(autoT('Dirección envío'), shipping.address)}
                  {detailFieldInline(autoT('Ciudad'), shipping.city)}
                  {detailFieldInline(autoT('Método de envío'), shipping.method)}
                  {detailFieldInline(
                    autoT('Nº Tracking'),
                    shipping.tracking,
                    { color: '#0070f3', fontWeight: 600, cursor: 'default' }
                  )}
                  {detailFieldInline(autoT('Costo de envío'), shipping.shippingCostFormatted)}
                  {detailFieldInline(autoT('Total de unidades por OC', {}), totalUnitsOcDisplay)}
                  {detailFieldInline(
                    autoT('Total Cant. Despachada', {}),
                    String(Math.round(Number.isFinite(totalShippedUnits) ? totalShippedUnits : 0))
                  )}
                  {detailFieldInline(
                    autoT('Total Cant. Devuelta', {}),
                    String(Math.round(Number.isFinite(totalReturnedUnits) ? totalReturnedUnits : 0))
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Líneas */}
          <Card style={{ borderRadius: cardRadius, boxShadow: cardShadow, marginBottom: '1rem' }}>
            <div style={{ padding: '1.25rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  paddingBottom: lines.length > 0 ? '0.75rem' : 0,
                  borderBottom: lines.length > 0 ? '1px solid #eef2f5' : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                  <Icon name="list" style={{ color: primaryBtn }} />
                  <Title level="H5" style={{ margin: 0 }}>
                    {autoT('Líneas de la Orden de Venta')}
                  </Title>
                </div>
                {lines.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '0.65rem',
                      flex: '1 1 auto',
                      minWidth: 0
                    }}
                  >
                    <Text style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
                      {autoT('Mostrando')}{' '}
                      <strong style={{ color: '#37474f' }}>
                        {linesRangeStart}–{linesRangeEnd}
                      </strong>{' '}
                      {autoT('de')} {lines.length}
                    </Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                      <Label style={{ margin: 0 }}>{autoT('Registros por página')}</Label>
                      <Select
                        value={String(linesPageSize)}
                        onChange={handleLinesPageSizeChange}
                        style={{ minWidth: '5.5rem' }}
                      >
                        {SALES_ORDER_LINES_PAGE_SIZES.map((n) => (
                          <Option key={n} value={String(n)}>
                            {String(n)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Button
                        design="Transparent"
                        disabled={linesPageSafe <= 1}
                        onClick={() => setLinesPage((p) => Math.max(1, p - 1))}
                      >
                        {autoT('Anterior')}
                      </Button>
                      <Text style={{ fontSize: '0.8125rem', color: '#37474f', minWidth: '7rem', textAlign: 'center' }}>
                        {autoT('Página')} {linesPageSafe} / {linesTotalPages}
                      </Text>
                      <Button
                        design="Transparent"
                        disabled={linesPageSafe >= linesTotalPages}
                        onClick={() => setLinesPage((p) => Math.min(linesTotalPages, p + 1))}
                      >
                        {autoT('Siguiente')}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse',
                    fontSize: '0.875rem'
                  }}
                >
                  <colgroup>
                    <col style={{ width: '44px' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '72px' }} />
                    <col style={{ width: '104px' }} />
                    <col style={{ width: '104px' }} />
                    <col style={{ width: '104px' }} />
                    <col style={{ width: '10%' }} />
                    {/* <col style={{ width: '9%' }} /> Descuento — oculto temporalmente */}
                    <col style={{ width: '10%' }} />
                    {/* <col style={{ width: '52px' }} /> Estado — oculto temporalmente */}
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e8ecef' }}>
                      {[
                        { label: '#', align: 'center' },
                        { label: autoT('SKU IAC'), align: 'left' },
                        { label: autoT('Descripción'), align: 'left' },
                        ...(showLineDestinationBranch
                          ? [{ label: autoT('Sucursal destino'), align: 'left' }]
                          : []),
                        { label: autoT('Cantidad'), align: 'center' },
                        { label: autoT('Cant. Despachada'), align: 'center' },
                        { label: autoT('Cant. Devuelta'), align: 'center' },
                        { label: autoT('Cant. Quiebre'), align: 'center' },
                        { label: autoT('Precio Unit.'), align: 'right' },
                        /* { label: autoT('Descuento'), align: 'right' }, — oculto temporalmente */
                        { label: autoT('Total Línea'), align: 'right' }
                        /* { label: autoT('Estado'), align: 'center' } — oculto temporalmente */
                      ].map((col, colIdx) => (
                        <th
                          key={col.label}
                          style={{
                            textAlign: col.align,
                            padding: '10px 8px',
                            color: '#78909c',
                            fontWeight: 600,
                            whiteSpace: 'normal',
                            lineHeight: 1.25,
                            verticalAlign: 'bottom'
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linesPaged.map((line, i) => (
                      <tr
                        key={`${line.lineNo}-${linesPageSafe}-${i}`}
                        style={{
                          background: (linesRangeStart - 1 + i) % 2 === 1 ? '#f8fbfd' : '#fff',
                          borderBottom: '1px solid #eef2f5'
                        }}
                      >
                        <td style={{ padding: '12px 8px', textAlign: 'center', verticalAlign: 'top' }}>
                          {line.lineNo}
                        </td>
                        <td style={{ padding: '12px 8px', verticalAlign: 'top', wordBreak: 'break-word' }}>
                          <span style={{ color: '#0070f3', fontWeight: 600, textDecoration: 'underline' }}>
                            {line.skuIac}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            verticalAlign: 'top',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={typeof line.description === 'string' ? line.description : ''}
                        >
                          {line.description}
                        </td>
                        {showLineDestinationBranch ? (
                          <td
                            style={{
                              padding: '12px 8px',
                              verticalAlign: 'top',
                              wordBreak: 'break-word'
                            }}
                          >
                            {line.destinationBranch || '—'}
                          </td>
                        ) : null}
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {line.quantity}
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {line.shippedQuantity ?? 0}
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {line.returnedQuantity ?? 0}
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {line.brokeQuantity ?? 0}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {line.unitPrice}
                        </td>
                        {/* Columna Descuento — oculto temporalmente
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap',
                            color: line.discount ? '#e65100' : 'inherit'
                          }}
                        >
                          {line.discount || '—'}
                        </td>
                        */}
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            fontWeight: 600,
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {line.lineTotal}
                        </td>
                        {/* Columna Estado — oculto temporalmente
                        <td
                          style={{
                            padding: '8px 4px',
                            verticalAlign: 'top',
                            width: '52px',
                            maxWidth: '52px',
                            textAlign: 'center'
                          }}
                        >
                          {pill(line.lineStatus, '#2e7d32', '#fff')}
                        </td>
                        */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          {/* Resumen financiero */}
          <Card style={{ borderRadius: cardRadius, boxShadow: cardShadow }}>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Icon name="batch-payments" style={{ color: primaryBtn }} />
                <Title level="H5" style={{ margin: 0 }}>
                  {autoT('Resumen Financiero')}
                </Title>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: '1rem',
                  alignItems: 'stretch'
                }}
              >
                {[
                  {
                    key: 'subtotal',
                    label: autoT('Subtotal (neto)', {}),
                    value: subtotalNetoDisplay
                  },
                  {
                    key: 'tax',
                    label: autoT('Impuesto', {}),
                    value: impuestoDisplay
                  },
                  {
                    key: 'total',
                    label: autoT('Total', {}),
                    value: totalConIvaDisplay,
                    highlight: true
                  },
                  { key: 'paid', label: autoT('Pagado', {}), value: finance.paid, paid: true },
                  {
                    key: 'units',
                    label: autoT('Cantidad unidades OV', {}),
                    value: totalUnitsToShipDisplay,
                    units: true
                  }
                ].map((box) => (
                  <div
                    key={box.key}
                    style={{
                      minWidth: 0,
                      padding: '1rem 1.2rem',
                      borderRadius: 10,
                      background: box.highlight ? '#e3f2fd' : '#f1f3f5',
                      border: box.highlight ? '2px solid #64b5f6' : '1px solid #dde2e7',
                      boxShadow: box.highlight ? '0 1px 4px rgba(25, 118, 210, 0.12)' : 'none'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        gap: '0.4rem',
                        columnGap: '0.55rem'
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          color: '#37474f',
                          flexShrink: 0
                        }}
                      >
                        {box.label}:
                      </Text>
                      <Text
                        style={{
                          fontSize: box.highlight ? '1.2rem' : box.units ? '1.1rem' : '1.05rem',
                          fontWeight: 700,
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                          color: box.paid
                            ? '#1b5e20'
                            : box.highlight
                              ? '#0d47a1'
                              : box.units
                                ? '#1565c0'
                                : '#1a2b3c'
                        }}
                      >
                        {box.value}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
      {renderTrackingModal()}

      <Dialog
        open={isCancelOrderDialogOpen}
        headerText=""
        style={{ width: 'min(480px, 94vw)', zIndex: 11000 }}
        onAfterClose={() => !cancelOrderLoading && closeCancelOrderDialog()}
        footer={
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0.65rem 1rem',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              borderTop: '1px solid #e0e0e0',
              background: '#fafafa'
            }}
          >
            <Button design="Transparent" onClick={closeCancelOrderDialog} disabled={cancelOrderLoading}>
              {autoT('No, cancelar', {})}
            </Button>
            <Button
              design="Emphasized"
              onClick={handleConfirmCancelOrderFromDetail}
              disabled={cancelOrderLoading}
              style={{ backgroundColor: '#c62828', color: '#fff' }}
            >
              {cancelOrderLoading ? autoT('Procesando...') : autoT('Sí, cancelar órdenes', {})}
            </Button>
          </div>
        }
      >
        <div style={{ padding: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem',
              padding: '0.85rem 1rem',
              background: '#b71c1c',
              color: '#fff',
              margin: 0
            }}
          >
            <Icon name="message-warning" style={{ color: '#ffccbc', flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>{autoT('Cancelar órdenes', {})}</span>
          </div>
          <div style={{ padding: '1.25rem 1.25rem 0.5rem', textAlign: 'center' }}>
            <Text style={{ display: 'block', fontSize: '1.05rem', fontWeight: 600, color: '#263238', marginBottom: '0.75rem' }}>
              {autoT(
                '¿Estás seguro de que deseas cancelar las órdenes seleccionadas? Esta acción no se puede deshacer.'
              )}
            </Text>
            <Text style={{ display: 'block', color: '#455a64', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {autoT('Órdenes afectadas', {})}: <strong style={{ color: '#b71c1c' }}>1</strong>
            </Text>
          </div>
        </div>
      </Dialog>

      {showCancelOrderResultDialog && cancelOrderResultSummary && (
        <Dialog
          open={showCancelOrderResultDialog}
          headerText={autoT('Resultado de la cancelación')}
          onAfterClose={() => {
            setShowCancelOrderResultDialog(false);
            setCancelOrderResultSummary(null);
          }}
          footer={
            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                design="Emphasized"
                onClick={() => {
                  setShowCancelOrderResultDialog(false);
                  setCancelOrderResultSummary(null);
                }}
              >
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem', minWidth: '320px', maxHeight: '360px', overflowY: 'auto' }}>
            <Text style={{ display: 'block', marginBottom: '0.75rem', color: '#37474f' }}>
              {autoT('Exitosas', {})}:{' '}
              <strong style={{ color: '#2e7d32' }}>{cancelOrderResultSummary.successCount}</strong>
              {' · '}
              {autoT('Con error', {})}:{' '}
              <strong style={{ color: '#c62828' }}>{cancelOrderResultSummary.failures.length}</strong>
            </Text>
            {cancelOrderResultSummary.failures.map((f) => (
              <div
                key={f.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #eee',
                  fontSize: '0.85rem'
                }}
              >
                <Text style={{ fontWeight: 700 }}>{f.id}</Text>
                <Text style={{ display: 'block', color: '#c62828' }}>{f.errorMessage}</Text>
              </div>
            ))}
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default SalesOrderDetail;
